import { apiClient } from './client';
import type { 
  User, 
  UserRelation, 
  CreateRelationRequest, 
  CreateVirtualTraineeRequest,
  UpdateVirtualTraineeRequest,
  UserStatistics,
  UserPreferences,
  UpdateUserPreferencesRequest
} from '../types/api';

export const userApi = {
  /**
   * Get current authenticated user
   */
  async getCurrentUser(): Promise<User> {
    return apiClient.get<User>('/v1/users/me');
  },

  /**
   * Get user by ID
   */
  async getUserById(userId: number): Promise<User> {
    return apiClient.get<User>(`/v1/users/${userId}`);
  },

  /**
   * Get current user's trainees
   */
  async getTrainees(): Promise<UserRelation[]> {
    return apiClient.get<UserRelation[]>('/v1/coach/trainees');
  },

  /**
   * Get current user's coaches
   */
  async getCoaches(): Promise<UserRelation[]> {
    return apiClient.get<UserRelation[]>('/v1/trainee/coaches');
  },

  /**
   * Invite a trainee by username
   */
  async inviteTrainee(request: CreateRelationRequest): Promise<UserRelation[]> {
    return apiClient.post<UserRelation[]>('/v1/coach/invite-trainee', request);
  },

  /**
   * Delete trainee relationship (coach endpoint)
   */
  async deleteTrainee(traineeId: number): Promise<void> {
    return apiClient.delete<void>(`/v1/coach/trainee/${traineeId}`);
  },

  /**
   * Delete coach relationship (trainee endpoint)
   */
  async deleteCoach(coachId: number): Promise<void> {
    return apiClient.delete<void>(`/v1/trainee/coach/${coachId}`);
  },

  /**
   * Accept coach invitation (trainee endpoint)
   */
  async acceptCoachInvite(coachId: number): Promise<void> {
    return apiClient.put<void>(`/v1/trainee/coach/${coachId}/accept`);
  },

  /**
   * Decline coach invitation (trainee endpoint)
   */
  async declineCoachInvite(coachId: number): Promise<void> {
    return apiClient.put<void>(`/v1/trainee/coach/${coachId}/decline`);
  },

  /**
   * Get user statistics for both coach and trainee roles
   */
  async getUserStatistics(): Promise<UserStatistics> {
    return apiClient.get<UserStatistics>('/v1/users/statistics');
  },

  /**
   * Create a virtual trainee (non-Telegram user)
   */
  async createVirtualTrainee(request: CreateVirtualTraineeRequest): Promise<User> {
    return apiClient.post<User>('/v1/coach/virtual-trainees', request);
  },

  /**
   * Update a virtual trainee
   */
  async updateVirtualTrainee(traineeId: number, request: UpdateVirtualTraineeRequest): Promise<User> {
    return apiClient.put<User>(`/v1/coach/virtual-trainees/${traineeId}`, request);
  },

  /**
   * Delete a virtual trainee
   */
  async deleteVirtualTrainee(traineeId: number): Promise<void> {
    return apiClient.delete<void>(`/v1/coach/virtual-trainees/${traineeId}`);
  },

  /**
   * Get user preferences
   */
  async getUserPreferences(): Promise<UserPreferences> {
    return apiClient.get<UserPreferences>('/v1/users/preferences');
  },

  /**
   * Update user preferences
   */
  async updateUserPreferences(request: UpdateUserPreferencesRequest): Promise<UserPreferences> {
    return apiClient.put<UserPreferences>('/v1/users/preferences', request);
  },

  /**
   * Manually sync user photo from Telegram
   */
  async syncPhoto(): Promise<{ status: string; message: string }> {
    return apiClient.post<{ status: string; message: string }>('/v1/users/preferences/sync-photo', {});
  },
};
